import React, { FormEventHandler, useCallback, useMemo, useState } from 'react'
import { Artifact } from '@trustero/trustero-api-web/lib/artifact/artifact_pb'
import log from 'loglevel'
import { ModalForm, ModalFormId } from 'src/components/ModalForms'
import {
  useHideModal,
  useIsShowModal,
  useModalState,
} from 'src/Modal/ModalStateContext'
import { TextInput } from 'src/components/Reusable/Inputs/TextInput'
import { FlexAlign, FlexColumn } from 'src/components/Reusable/Flex'
import { CsvDataGridContainer } from 'src/components/ModalForms/Evidence/evidence.styles'
import { EvidenceDataGrid } from 'src/pages/Evidence/show/tabs/evidence/AutomaticEvidence/AutomaticEvidenceBody'
import { getEvidenceCsv } from 'src/Utils/Evidence/evidence.helpers'
import { convertRowSecondsToISODate } from 'src/Utils/grid/grid.helpers'
import { showInfoToast } from 'src/Utils/helpers/toast'
import { useInvalidateEvidenceGroups } from 'src/pages/Evidence/evidence.hooks'
import { LinkEvidenceToControlsModal } from 'src/pages/Evidence/modals/LinkEvidenceToControlsModal'
import { useSaveArtifactAsEvidence } from '../artifacts/Artifact.hooks'
import { useArtifactModalContext } from '../artifacts/ArtifactModalContext'
import {
  getArtifactCsv,
  getArtifactDescription,
  getArtifactPreviewDescription,
  getTestTableHeaderTooltip,
  getHighlightRowClassName,
} from '../artifacts/Artifacts.helpers'

const ArtifactTablePreview = ({
  csv,
  artifact,
}: {
  csv: string
  artifact: Artifact
}): JSX.Element => {
  const headerTooltip = useMemo(
    () => getTestTableHeaderTooltip(artifact),
    [artifact],
  )
  const getRowClassName = useMemo(
    () => getHighlightRowClassName(artifact),
    [artifact],
  )
  const description = useMemo(
    () => getArtifactPreviewDescription(artifact),
    [artifact],
  )

  const { rows: rawRows, columns } = getEvidenceCsv(csv, headerTooltip)
  const rows = useMemo(
    () => rawRows.map((row) => convertRowSecondsToISODate(row)),
    [rawRows],
  )

  if (!csv || rows.length === 0) {
    return <></>
  }

  return (
    <>
      {description}
      <CsvDataGridContainer $isModal>
        <EvidenceDataGrid
          rows={rows}
          columns={columns}
          getRowClassName={getRowClassName}
        />
      </CsvDataGridContainer>
    </>
  )
}

export const SaveArtifactAsEvidenceModal = ({
  artifact,
}: {
  artifact: Artifact
}): JSX.Element => {
  const { openModal } = useModalState()
  const { markArtifactSaved } = useArtifactModalContext()
  const [createdEvidenceIds, setCreatedEvidenceIds] = useState<string[]>([])
  const [caption, setCaption] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const saveArtifactAsEvidence = useSaveArtifactAsEvidence()
  const invalidateEvidenceGroups = useInvalidateEvidenceGroups()
  const show = useIsShowModal(ModalFormId.SAVE_ARTIFACT_AS_EVIDENCE)
  const hide = useHideModal({
    modalId: ModalFormId.SAVE_ARTIFACT_AS_EVIDENCE,
    onHide: () => setCreatedEvidenceIds([]),
  })

  const csv = useMemo(() => getArtifactCsv(artifact), [artifact])

  const onSubmit: FormEventHandler = useCallback(
    async (e: React.MouseEvent<HTMLFormElement>) => {
      e.preventDefault()
      const evidenceCaption = caption.trim()
      if (!evidenceCaption) return
      try {
        setIsSubmitting(true)
        const description = getArtifactDescription(artifact)
        const { evidenceId, evidenceInstanceId } = await saveArtifactAsEvidence(
          artifact.getId(),
          evidenceCaption,
          description,
        )
        setCreatedEvidenceIds([evidenceId])
        markArtifactSaved(artifact.getId(), evidenceInstanceId)
        await invalidateEvidenceGroups()
        openModal(ModalFormId.LINK_ARTIFACT_EVIDENCE_TO_CONTROLS)
      } catch (err) {
        log.error('Error saving artifact as evidence', err)
        showInfoToast('Error saving artifact as evidence')
      } finally {
        setIsSubmitting(false)
      }
    },
    [
      artifact,
      caption,
      saveArtifactAsEvidence,
      invalidateEvidenceGroups,
      openModal,
      markArtifactSaved,
    ],
  )

  const onHideFunc = () => {
    setCreatedEvidenceIds([])
  }

  return (
    <>
      <ModalForm
        show={show}
        hide={hide}
        formId={ModalFormId.SAVE_ARTIFACT_AS_EVIDENCE}
        title="Save as Evidence"
        submitText="Link to Controls"
        description="Save this artifact as evidence and link it to controls"
        busy={isSubmitting}
        disableSubmitButton={isSubmitting || !caption.trim()}
        size="xl"
      >
        <form id={ModalFormId.SAVE_ARTIFACT_AS_EVIDENCE} onSubmit={onSubmit}>
          <FlexColumn gap={16} align={FlexAlign.STRETCH}>
            <TextInput
              name="caption"
              label="Caption"
              initVal=""
              placeholder="Add Caption for Evidence"
              onChangeCb={setCaption}
              form={ModalFormId.SAVE_ARTIFACT_AS_EVIDENCE}
            />
            <ArtifactTablePreview csv={csv} artifact={artifact} />
          </FlexColumn>
        </form>
      </ModalForm>
      <LinkEvidenceToControlsModal
        createdEvidenceIds={createdEvidenceIds}
        onHideFunc={onHideFunc}
        formId={ModalFormId.LINK_ARTIFACT_EVIDENCE_TO_CONTROLS}
      />
    </>
  )
}
